import { useEffect, useRef } from "react";

import { estEnDemo } from "@/core/demo/actif";
import { abonner as abonnerAuBusDemo } from "@/core/demo/bus";

export interface SSEOptions<T> {
  /** Full URL of the SSE endpoint (passed to EventSource). */
  url: string;
  /** Called for every event received and JSON-parsed. */
  onEvent: (event: T) => void;
  /** If false, the connection is not opened. */
  enabled?: boolean;
  /** Base reconnect delay in ms — the floor of the exponential backoff (default 3000). */
  reconnectDelay?: number;
  /** Upper bound of the backoff in ms (default 30000). */
  maxReconnectDelay?: number;
  /** Called when the connection RE-opens after a drop (not the first open).
   *  Use to refetch state missed during the gap — SSE / Redis pub-sub has no
   *  replay, so events published while disconnected (e.g. a backend rollout)
   *  are lost otherwise. */
  onReconnect?: () => void;
  /** Specific event name to subscribe to (default "message"). */
  eventName?: string;
  /** Send credentials with the request (default true). */
  withCredentials?: boolean;
  /** When true, close the stream while the document is hidden (backgrounded
   *  tab / PWA) and reopen it on return — saving CPU/battery on mobile and
   *  avoiding event fan-out nobody is watching. The reopen counts as a
   *  reconnect, so `onReconnect` fires and the caller can backfill what was
   *  published during the gap. Default false (stream stays open in background). */
  closeWhenHidden?: boolean;
}

/** Once a connection has stayed open this long, it counts as "stable" and the
 *  backoff attempt counter resets (so a brief later blip starts from the floor,
 *  not from a high accumulated delay). */
export const STABLE_CONNECTION_RESET_MS = 60_000;

/**
 * "Equal jitter" exponential backoff delay for SSE reconnection (finding C2).
 *
 *   cap   = min(maxReconnectDelay, baseDelay · 2^attempt)
 *   delay ∈ [cap/2, cap]   (half deterministic, half random)
 *
 * The deterministic half guarantees a growing minimum spacing under repeated
 * failure; the random half DECORRELATES reconnections across clients so a mass
 * drop (backend rollout, network blip) does not trigger a synchronized
 * reconnect storm. `attempt` is 0-based (first retry uses attempt 0).
 *
 * Pure + injectable `rand` for deterministic tests.
 */
export function computeBackoffDelay(
  attempt: number,
  baseDelay: number,
  maxReconnectDelay: number,
  rand: () => number = Math.random,
): number {
  // Clamp the exponent so `2 ** attempt` can't overflow to Infinity on a long
  // outage; min() with the cap makes anything past the cap a no-op anyway.
  const safeAttempt = Math.max(0, Math.min(attempt, 30));
  const cap = Math.min(maxReconnectDelay, baseDelay * 2 ** safeAttempt);
  return cap / 2 + rand() * (cap / 2);
}

/**
 * Lightweight EventSource wrapper with auto-reconnect.
 *
 * Ported pattern from `simscope/src/hooks/useProdSSE.ts`. Generalized so
 * any panel can subscribe to a backend SSE stream without re-implementing
 * reconnect / cleanup.
 *
 * Reconnection uses exponential backoff + equal jitter (see
 * `computeBackoffDelay`): the delay grows per consecutive failure and is
 * randomized to avoid a synchronized reconnect storm after a mass disconnect.
 * The attempt counter resets once a connection has stayed open for
 * `STABLE_CONNECTION_RESET_MS`. The connection never gives up — under a long
 * outage it keeps retrying at the capped (`maxReconnectDelay`) jittered delay,
 * and `onReconnect` lets the caller backfill what it missed on each recovery.
 *
 * Limitations:
 *   - Single event name. For multiplexed streams, register multiple hooks.
 */
export function useSSE<T>(opts: SSEOptions<T>) {
  const {
    url,
    onEvent,
    enabled = true,
    reconnectDelay = 3000,
    maxReconnectDelay = 30_000,
    eventName = "message",
    withCredentials = true,
    onReconnect,
    closeWhenHidden = false,
  } = opts;

  const sourceRef = useRef<EventSource | null>(null);
  const onEventRef = useRef(onEvent);
  onEventRef.current = onEvent;
  const onReconnectRef = useRef(onReconnect);
  onReconnectRef.current = onReconnect;

  useEffect(() => {
    if (!enabled) return;

    // 🎬 Mode démo : le direct vient du bus local au lieu du réseau. Le
    // consommateur ne voit aucune différence — mêmes événements, mêmes noms de
    // champs — parce que c'est exactement ce qu'on veut montrer à l'image.
    if (estEnDemo() && url.includes("/api/conversations/events")) {
      return abonnerAuBusDemo((evenement) => onEventRef.current(evenement as T));
    }

    let cancelled = false;
    let connectedOnce = false;
    // Consecutive failed-connection counter that drives the backoff. Reset to 0
    // once a connection proves stable (see stableTimer).
    let attempt = 0;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    let stableTimer: ReturnType<typeof setTimeout> | null = null;
    // True while the stream is intentionally closed because the document is
    // hidden (closeWhenHidden) — distinct from `cancelled` (effect teardown).
    let closedByHidden = false;

    function clearStableTimer() {
      if (stableTimer) {
        clearTimeout(stableTimer);
        stableTimer = null;
      }
    }

    function clearReconnectTimer() {
      if (reconnectTimer) {
        clearTimeout(reconnectTimer);
        reconnectTimer = null;
      }
    }

    function connect() {
      const es = new EventSource(url, { withCredentials });
      sourceRef.current = es;

      es.addEventListener("open", () => {
        // The first open is the initial connection; any subsequent open is a
        // recovery after a drop → let the caller backfill what it missed.
        if (connectedOnce) onReconnectRef.current?.();
        connectedOnce = true;
        // Only reset the backoff AFTER the connection has held for a while — an
        // open that immediately errors again should keep backing off, otherwise
        // a flapping endpoint reconnects at the floor delay forever.
        clearStableTimer();
        stableTimer = setTimeout(() => {
          attempt = 0;
        }, STABLE_CONNECTION_RESET_MS);
      });

      es.addEventListener(eventName, (ev) => {
        try {
          const parsed = JSON.parse((ev as MessageEvent).data) as T;
          onEventRef.current(parsed);
        } catch {
          // Ignore malformed events — caller can use raw `addEventListener` if needed.
        }
      });

      es.addEventListener("error", () => {
        es.close();
        clearStableTimer();
        // Don't reconnect if we're torn down or deliberately closed (hidden) —
        // the visibility handler owns reopening in the latter case.
        if (cancelled || closedByHidden) return;
        const delay = computeBackoffDelay(
          attempt,
          reconnectDelay,
          maxReconnectDelay,
        );
        attempt += 1;
        reconnectTimer = setTimeout(connect, delay);
      });
    }

    // Close in background, reopen on return. A native `close()` does NOT emit an
    // "error" event, so it won't trigger the reconnect path; the reopen runs
    // `connect()` fresh and (connectedOnce already true) fires `onReconnect`.
    function handleVisibility() {
      if (document.hidden) {
        closedByHidden = true;
        clearReconnectTimer();
        clearStableTimer();
        sourceRef.current?.close();
        sourceRef.current = null;
      } else if (closedByHidden) {
        closedByHidden = false;
        attempt = 0;
        connect();
      }
    }

    connect();
    if (closeWhenHidden) {
      document.addEventListener("visibilitychange", handleVisibility);
    }

    return () => {
      cancelled = true;
      clearReconnectTimer();
      clearStableTimer();
      if (closeWhenHidden) {
        document.removeEventListener("visibilitychange", handleVisibility);
      }
      sourceRef.current?.close();
      sourceRef.current = null;
    };
  }, [url, enabled, reconnectDelay, maxReconnectDelay, eventName, withCredentials, closeWhenHidden]);
}
